import { useEffect, useMemo, useRef, useState } from "react";
import { DateTime, Settings } from "luxon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faArrowRight } from "@fortawesome/free-solid-svg-icons";

Settings.defaultLocale = "el";

const FIELD_LABELS = {
  gak: "ΓΑΚ",
  year: "Έτος",
  court: "Δικαστήριο",
  imerominia_katathesis: "Ημ/νία κατάθεσης",
  eak: "ΕΑΚ",
  diadikasia: "Διαδικασία",
  antikeimeno: "Αντικείμενο",
  eidos: "Είδος",
  arithmos_pinakiou: "Πινάκιο",
  arithmos_apofasis: "Αρ. Απόφασης",
  apotelesma: "Αποτέλεσμα",
  sfalma: "Λειτουργική κατάσταση",
  last_checked: "Τελευταίος έλεγχος",
};

const SNAPSHOT_SECTIONS = [
  {
    title: "Ταυτότητα",
    fields: ["gak", "year", "court", "eak"],
  },
  {
    title: "Διαδικασία",
    fields: ["diadikasia", "eidos", "antikeimeno"],
  },
  {
    title: "Χρονολόγιο και έκβαση",
    fields: [
      "imerominia_katathesis",
      "arithmos_pinakiou",
      "arithmos_apofasis",
      "apotelesma",
    ],
  },
];

function normalizeValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).replace(/\u00a0/g, " ").trim();
}

function displayValue(value) {
  const normalized = normalizeValue(value);
  return normalized === "" ? "—" : normalized;
}

function formatHistoryDate(value, includeTime = true) {
  if (!value) {
    return "—";
  }

  const parsed = DateTime.fromSQL(value);
  if (!parsed.isValid) {
    return value;
  }

  const normalized = parsed.plus({
    minutes: Math.abs(new Date().getTimezoneOffset()),
  });

  if (!includeTime) {
    return normalized.toLocaleString(DateTime.DATE_FULL);
  }

  return `${normalized.toLocaleString(DateTime.DATE_FULL)} · ${normalized.toFormat("HH:mm")}`;
}

function getChangeVersions(versions) {
  return versions.filter((version) => version.source === "change");
}

function safeChangedFields(version) {
  return Array.isArray(version?.changed_fields) ? version.changed_fields : [];
}

const HEADLINE_RULES = [
  { fields: ["apotelesma", "arithmos_apofasis"], headline: "Ενημέρωση αποτελέσματος" },
  { fields: ["arithmos_pinakiou", "imerominia_katathesis"], headline: "Ενημέρωση δικασίμου / καταχώρισης" },
  { fields: ["diadikasia", "eidos", "antikeimeno"], headline: "Ενημέρωση διαδικασίας" },
  { fields: ["gak", "year", "court", "eak"], headline: "Ενημέρωση βασικών στοιχείων" },
];

function getVersionHeadline(version) {
  if (version.source === "baseline") return "Αρχικό καθαρό στιγμιότυπο";
  const fields = safeChangedFields(version);
  const matched = HEADLINE_RULES.find((rule) =>
    rule.fields.some((f) => fields.includes(f))
  );
  return matched?.headline || "Ενημέρωση υπόθεσης";
}

function getVersionSubtitle(version) {
  if (version.source === "baseline") {
    return "Η πρώτη καθαρή εικόνα της υπόθεσης όπως αποθηκεύτηκε στο ιστορικό.";
  }

  const fields = safeChangedFields(version);
  if (!fields.length) {
    return "Δεν καταγράφηκαν πεδία αλλαγής για αυτήν την έκδοση.";
  }

  return fields.map((field) => FIELD_LABELS[field] || field).join(" • ");
}

function buildSnapshotSections(snapshot, options = {}) {
  const { fieldsToEmphasize = [] } = options;

  return SNAPSHOT_SECTIONS.map((section) => ({
    ...section,
    values: section.fields.map((field) => ({
      field,
      label: FIELD_LABELS[field] || field,
      value: displayValue(snapshot?.[field]),
      emphasized: fieldsToEmphasize.includes(field),
    })),
  }));
}

function CollapsibleSection({ open, children }) {
  return (
    <div className="history-collapsible" data-expanded={open}>
      <div className="history-collapsible-inner">{children}</div>
    </div>
  );
}

function isEmptyValue(value) {
  return normalizeValue(value) === "";
}

function HistorySnapshot({ snapshot, emphasizedFields = [] }) {
  const sections = buildSnapshotSections(snapshot, {
    fieldsToEmphasize: emphasizedFields,
  });

  return (
    <div className="history-snapshot-grid">
      {sections.map((section) => (
        <article key={section.title} className="history-snapshot-card">
          <p className="history-section-title">{section.title}</p>
          <dl className="history-snapshot-list">
            {section.values.map((item) => (
              <div
                key={item.field}
                className={`history-snapshot-item${
                  item.emphasized ? " history-snapshot-item--accent" : ""
                }`}
              >
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        </article>
      ))}
    </div>
  );
}

function HistoryDiff({ version }) {
  const changedFields = safeChangedFields(version);
  const diff = version?.diff || {};

  if (!changedFields.length) {
    return (
      <p className="history-card-empty">
        Δεν βρέθηκαν ουσιαστικές αλλαγές σε αυτήν την έκδοση.
      </p>
    );
  }

  return (
    <div className="history-diff-list">
      {changedFields.map((field) => {
        const change = diff?.[field] || {};

        return (
          <article key={field} className="history-diff-row">
            <div className="history-diff-label">
              <span>{FIELD_LABELS[field] || field}</span>
            </div>
            <div className="history-diff-values">
              <div className="history-diff-box history-diff-box--before">
                <p>Πριν</p>
                <strong className={isEmptyValue(change.from) ? "history-diff-value--empty" : ""}>
                  {displayValue(change.from)}
                </strong>
              </div>
              <div className="history-diff-arrow">
                <FontAwesomeIcon icon={faArrowRight} />
              </div>
              <div className="history-diff-box history-diff-box--after">
                <p>Μετά</p>
                <strong className={isEmptyValue(change.to) ? "history-diff-value--empty" : ""}>
                  {displayValue(change.to)}
                </strong>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

const AstikaHistoryTimeline = ({ historyData, historyError, loading }) => {
  const versions = useMemo(
    () => (Array.isArray(historyData?.versions) ? historyData.versions : []),
    [historyData]
  );
  const changeVersions = useMemo(() => getChangeVersions(versions), [versions]);
  const latestChangeVersion = useMemo(
    () => (changeVersions.length ? changeVersions[0] : null),
    [changeVersions]
  );

  const [expandedVersions, setExpandedVersions] = useState({});
  const [visibleSnapshots, setVisibleSnapshots] = useState({});
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!latestChangeVersion?.version_no) return;
    initializedRef.current = true;
    setExpandedVersions({ [latestChangeVersion.version_no]: true });
  }, [latestChangeVersion]);

  const toggleVersion = (versionNo) => {
    setExpandedVersions((current) => ({
      ...current,
      [versionNo]: !current[versionNo],
    }));
  };

  const toggleSnapshot = (versionNo) => {
    setVisibleSnapshots((current) => ({
      ...current,
      [versionNo]: !current[versionNo],
    }));
  };

  const summaryCards = [
    {
      label: "Ουσιαστικές αλλαγές",
      value: changeVersions.length,
    },
    {
      label: "Τελευταία αλλαγή",
      value: latestChangeVersion
        ? formatHistoryDate(latestChangeVersion.created_at)
        : "Καμμία ακόμη",
    },
  ];

  if (loading && !historyData) {
    return (
      <section className="solon-history">
        <div className="history-skeleton-row" />
        <div className="history-skeleton-row history-skeleton-row--short" />
        <div className="history-skeleton-row history-skeleton-row--medium" />
      </section>
    );
  }

  return (
    <section className="solon-history">
      <h2 className="history-title">Ιστορικό Υπόθεσης</h2>
      <div className="history-summary-row">
        {summaryCards.map((card) => (
          <span key={card.label} className="history-stat">
            <span className="history-stat-value">{card.value}</span>{" "}
            <span className="history-stat-label">{card.label}</span>
          </span>
        ))}
      </div>

      {historyError && (
        <p className="history-inline-alert history-inline-alert--warning">
          {historyError}
        </p>
      )}

      <div className="history-timeline">
        {changeVersions.length ? (
          changeVersions.map((version) => {
            const versionNo = version.version_no;
            const isExpanded = Boolean(expandedVersions[versionNo]);
            const isSnapshotVisible = Boolean(visibleSnapshots[versionNo]);
            const changedFields = safeChangedFields(version);

            return (
              <article
                key={versionNo}
                className={`history-card${
                  isExpanded ? " history-card--open" : ""
                }`}
              >
                <button
                  type="button"
                  className="history-card-toggle"
                  onClick={() => toggleVersion(versionNo)}
                >
                  <div className="history-card-header">
                    <h3 className="history-card-headline">
                      <span className="history-card-version">#{versionNo}</span>
                      {getVersionHeadline(version)}
                    </h3>
                    <span className={`history-chevron${isExpanded ? " history-chevron--open" : ""}`}>
                      <FontAwesomeIcon icon={faChevronDown} />
                    </span>
                  </div>
                  <div className="history-card-meta">
                    <span>{formatHistoryDate(version.created_at)}</span>
                    <span>
                      {changedFields.length} πεδία: {getVersionSubtitle(version)}
                    </span>
                  </div>
                </button>

                <CollapsibleSection open={isExpanded}>
                  <div className="history-card-body">
                    <HistoryDiff version={version} />

                    <div className="history-card-actions">
                      <button
                        type="button"
                        className="history-secondary-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSnapshot(versionNo);
                        }}
                      >
                        {isSnapshotVisible
                          ? "Απόκρυψη πλήρους στιγμιότυπου"
                          : "Προβολή πλήρους στιγμιότυπου"}
                      </button>
                    </div>

                    <CollapsibleSection open={isSnapshotVisible}>
                      <HistorySnapshot
                        snapshot={version.snapshot}
                        emphasizedFields={changedFields}
                      />
                    </CollapsibleSection>
                  </div>
                </CollapsibleSection>
              </article>
            );
          })
        ) : (
          <p className="history-inline-alert">
            Δεν βρέθηκαν αποθηκευμένες ιστορικές εκδόσεις για αυτήν την
            υπόθεση.
          </p>
        )}
      </div>
    </section>
  );
};

export default AstikaHistoryTimeline;
